import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Wrench, X } from "lucide-react";
import { getPrestations, createPrestation, getReservations, getLogements } from "@/lib/api";

const typeLabels = {
  remise_cles: "Remise des clés",
  etat_lieux_entree: "État des lieux entrée",
  etat_lieux_sortie: "État des lieux sortie",
  menage: "Ménage inter-séjour",
  linge: "Gestion du linge",
  reparation: "Réparation"
};

export default function PrestationsPage() {
  const [prestations, setPrestations] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [logements, setLogements] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    reservation_id: "",
    logement_id: "",
    type_prestation: "menage",
    description: "",
    cout: 0,
    date_realisation: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [prestRes, resRes, logRes] = await Promise.all([
        getPrestations(),
        getReservations({ statut: "confirmee" }),
        getLogements()
      ]);
      setPrestations(prestRes.data);
      setReservations(resRes.data);
      
      const logMap = {};
      logRes.data.forEach(l => logMap[l.id] = l);
      setLogements(logMap);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get logement_id from selected reservation
      const selectedRes = reservations.find(r => r.id === formData.reservation_id);
      const dataToSend = {
        ...formData,
        logement_id: selectedRes?.logement_id || formData.logement_id
      };
      await createPrestation(dataToSend);
      toast.success("Prestation enregistrée !");
      setShowModal(false);
      setFormData({
        reservation_id: "",
        logement_id: "",
        type_prestation: "menage",
        description: "",
        cout: 0,
        date_realisation: ""
      });
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="prestations-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-display text-[#004333]">Prestations</h2>
          <p className="text-gray-500">{prestations.length} prestation(s) enregistrée(s)</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="add-prestation-btn"
        >
          <Plus className="w-4 h-4" />
          Nouvelle prestation
        </button>
      </div>

      {prestations.length > 0 ? (
        <div className="bg-white border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left p-4 font-semibold text-[#004333]">Type</th>
                <th className="text-left p-4 font-semibold text-[#004333]">Logement</th>
                <th className="text-left p-4 font-semibold text-[#004333]">Date</th>
                <th className="text-left p-4 font-semibold text-[#004333]">Description</th>
                <th className="text-right p-4 font-semibold text-[#004333]">Coût</th>
              </tr>
            </thead>
            <tbody>
              {prestations.map((prest) => (
                <tr key={prest.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <span className="px-2 py-1 bg-[#004333]/10 text-[#004333] text-sm font-medium">
                      {typeLabels[prest.type_prestation] || prest.type_prestation}
                    </span>
                  </td>
                  <td className="p-4 text-gray-700">
                    {logements[prest.logement_id]?.titre || "N/A"}
                  </td>
                  <td className="p-4 text-gray-600">{prest.date_realisation}</td>
                  <td className="p-4 text-gray-600">{prest.description || "-"}</td>
                  <td className="p-4 text-right font-mono font-bold text-[#C9922A]">
                    {prest.cout} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-16 text-center">
          <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-[#004333] mb-2">Aucune prestation</h3>
          <p className="text-gray-500">Enregistrez les prestations effectuées pour chaque réservation.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">Nouvelle prestation</h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Réservation associée</label>
                <select
                  required
                  value={formData.reservation_id}
                  onChange={(e) => setFormData({ ...formData, reservation_id: e.target.value })}
                  className="input-hh"
                >
                  <option value="">Sélectionner une réservation</option>
                  {reservations.map((res) => (
                    <option key={res.id} value={res.id}>
                      {res.voyageur_nom} - {logements[res.logement_id]?.titre || "N/A"} ({res.date_arrivee})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de prestation</label>
                <select
                  value={formData.type_prestation}
                  onChange={(e) => setFormData({ ...formData, type_prestation: e.target.value })}
                  className="input-hh"
                >
                  {Object.entries(typeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date de réalisation</label>
                <input
                  type="date"
                  required
                  value={formData.date_realisation}
                  onChange={(e) => setFormData({ ...formData, date_realisation: e.target.value })}
                  className="input-hh"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Coût (€)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cout}
                  onChange={(e) => setFormData({ ...formData, cout: parseFloat(e.target.value) })}
                  className="input-hh"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-hh"
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
